import { Theme } from "@mui/system";
import { FieldSelectOption, HeadingLevel } from "@/core/types/types";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { FormGroup, Box, Typography } from "@mui/material";
import Field, { FieldProps } from "@/core/components/Field";
import { Controller } from "react-hook-form";
import { useFormContext } from "react-hook-form";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import ErrorMessage from "@/core/components/ErrorMessage/ErrorMessage";
import { useEffect } from "react";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";

const MULTIPLE_CHECKBOX: string = "multipleCheckbox";
const EXTRA_OPTION: string = "extraOption";
const EXTRA_OPTION_VALUE: string = "extraOptionValue";

const styles = (theme: Theme) => ({
  root: {
    display: "flex",
    gap: theme.spacing(1),
  },
  checkboxLabel: {
    color: theme.palette.grey[800],
    borderRadius: theme.spacing(0.5),
    "& > div": {
      width: "100%",
    },
    "& label": {
      boxSizing: "border-box",
      border: `1px solid ${theme.palette.secondary.main}`,
      backgroundColor: theme.palette.background.default,
      alignItems: "center",
      padding: theme.spacing(2),
      height: 48,
    },
    "& label:hover, & label:has(.Mui-checked)": {
      border: `1px solid ${theme.palette.primary.main}`,
    },
  },
});

const MultipleCheckbox = ({ field }: FieldProps) => {
  const {
    setValue,
    control,
    formState: { errors },
    clearErrors,
    watch,
  } = useFormContext();

  const classes: any = useCustomStyles(styles);
  const multipleCheckboxValuesField: string = field.key;
  const checkboxFields: FieldSelectOption[] | undefined = field.values;
  const multipleCheckboxValues: any = watch(MULTIPLE_CHECKBOX);
  const extraOptionValue: string | undefined = watch(EXTRA_OPTION_VALUE);
  const multipleCheckboxOptions: string[] = Object.keys(
    multipleCheckboxValues || []
  );
  const numberOfCheckedOptions: number = multipleCheckboxOptions.filter(
    (item: any) => multipleCheckboxValues[item] === true
  ).length;

  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );

  // Extra option is multiple checkbox extra feature, that should have a clean state after re-render, only if it exists
  useEffect(() => {
    if (multipleCheckboxValues?.[EXTRA_OPTION] === false) {
      clearErrors(EXTRA_OPTION_VALUE);
      setValue(EXTRA_OPTION_VALUE, undefined);
    }
  }, [multipleCheckboxValues?.[EXTRA_OPTION]]);

  // Each time the number of checked options is changed - reassemble the result array
  useEffect(() => {
    const arrayOfCheckedOptions: string[] = multipleCheckboxOptions
      .map((item) => (multipleCheckboxValues[item] === true ? item : ""))
      .filter((option) => option !== "");

    // Add extra options textfield value INSTEAD of `extraOption` itself
    if (extraOptionValue) {
      const extraOptionIndex = arrayOfCheckedOptions.indexOf(EXTRA_OPTION);
      arrayOfCheckedOptions.splice(extraOptionIndex, 1);
      arrayOfCheckedOptions.push(extraOptionValue);
    }

    if (numberOfCheckedOptions !== 0) {
      setValue(MULTIPLE_CHECKBOX, multipleCheckboxValues);
      setValue(multipleCheckboxValuesField, arrayOfCheckedOptions);
      clearErrors(MULTIPLE_CHECKBOX);
    } else {
      setValue(MULTIPLE_CHECKBOX, undefined);
      setValue(multipleCheckboxValuesField, []);
    }
  }, [numberOfCheckedOptions, extraOptionValue]);

  const validateMultipleCheckboxValues = () => {
    if (field.validate?.required && numberOfCheckedOptions === 0) {
      return `validate.${errorTranslationKey}.required.message`;
    } else {
      return true;
    }
  };

  return (
    <Controller
      control={control}
      name={field.key}
      rules={{
        validate: validateMultipleCheckboxValues,
      }}
      render={() => (
        <Box>
          <Box mb={2}>
            <Typography variant={HeadingLevel.Body1}>
              {t(`${field.label}`)}
            </Typography>
          </Box>
          <FormGroup sx={classes?.root} data-cy={field.key}>
            {checkboxFields?.length ? (
              checkboxFields.map((item, index) => (
                <Box key={index} sx={classes?.checkboxLabel}>
                  <Field
                    field={{
                      key: `${MULTIPLE_CHECKBOX}.${item.value}`,
                      label: item.label,
                      id: item.value,
                      type: "checkbox",
                      properties: {},
                    }}
                  ></Field>
                </Box>
              ))
            ) : (
              <ErrorMessage
                text={t("validate.multipleCheckbox.missingValues.message")}
              />
            )}
          </FormGroup>
          {hasError && (
            <Box mt={1}>
              <ErrorMessage text={`${errorMessage}`} />
            </Box>
          )}
        </Box>
      )}
    ></Controller>
  );
};

export default MultipleCheckbox;
